import { MenuItem, TextField, Alert, Box, Chip } from '@mui/material'
import Grid from '@mui/material/Grid'
import {
	GridContainer,
	PageContainer,
	PageTop,
	SearchContainer,
	StyledDataGrid,
} from 'components/ApplicationStyles'
import PagePosition from 'components/PagePosition'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
	GridColDef,
	GridRowSelectionModel,
	useGridApiRef,
} from '@mui/x-data-grid-premium'
import {
	apiBoardDeleteMultiple,
	apiBoardList,
	apiBoardSave,
	apiBoardView,
} from 'api/api'
import {
	BoardDetailType,
	BoardSearchType,
	BoardType,
	CmmnCodeType,
	FileItemType,
} from 'constants/types'
import { koKR } from '@mui/x-data-grid-premium/locales'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import BoardWrite from 'components/BoardWrite'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { boardSchema, boardDefaultValues } from 'schemas'
import { alertModal, confirmAlert, timerAlert } from 'util/util'
import BoardView from 'components/BoardView'
import { useSelector } from 'react-redux'
import { selectCmmnCode } from 'hooks/cmmnCodeFactory'
import { extractCmmnCode } from 'util/cmmnCodeUtil'

// React Query 키 팩토리
const qnaKeys = {
	all: ['qna'] as const,
	lists: () => [...qnaKeys.all, 'list'] as const,
	list: (params: BoardSearchType) => [...qnaKeys.lists(), params] as const,
}

const Qna = () => {
	const cmmdCd = useSelector(selectCmmnCode)
	const [boardTypeList, setBoardTypeList] = useState<CmmnCodeType[]>([])

	const gridColumns: GridColDef[] = useMemo(
		() => [
			{
				field: 'id',
				headerName: 'id',
				width: 90,
			},
			{
				field: 'rowNumber',
				headerName: '번호',
				width: 90,
				type: 'number',
			},
			{
				field: 'code',
				headerName: '분류',
				width: 150,
				type: 'string',
				renderCell: (params) => {
					// code 값을 boardTypeList에서 찾아서 이름 표시
					const codeItem = boardTypeList.find((item) => item.cmmnCd === params.value)
					return <span>{codeItem ? codeItem.cmmnNm : params.value}</span>
				},
			},
			{
				field: 'title',
				headerName: '제목',
				flex: 1,
				minWidth: 150,
				renderCell: (params) => {
					const { startDate, endDate, code, isDeleted } = params.row as BoardType
					let dateCompare: React.ReactNode = null
					// startDate와 endDate가 모두 지금 날짜와 비교해서 dateCompare 완성
					const now = new Date()
					if (startDate && endDate) {
						const start = new Date(startDate)
						const end = new Date(endDate)
						if (now < start) {
							dateCompare = (
								<Chip
									label="예정"
									color="primary"
								/>
							)
						} else if (now > end) {
							dateCompare = (
								<Chip
									label="종료"
									color="default"
								/>
							)
						} else {
							dateCompare = (
								<Chip
									label="진행중"
									color="success"
								/>
							)
						}
					} else if (startDate && !endDate) {
						const start = new Date(startDate)
						if (now < start) {
							dateCompare = (
								<Chip
									label="예정"
									color="primary"
								/>
							)
						} else {
							dateCompare = (
								<Chip
									label="진행중"
									color="success"
								/>
							)
						}
					} else if (!startDate && endDate) {
						const end = new Date(endDate)
						if (now > end) {
							dateCompare = (
								<Chip
									label="종료"
									color="default"
								/>
							)
						} else {
							dateCompare = (
								<Chip
									label="진행중"
									color="success"
								/>
							)
						}
					}

					return (
						<button
							type="button"
							onClick={() => onTitleClick(params.id)}
							style={{
								background: 'none',
								border: 'none',
								outline: 'none',
								cursor: 'pointer',
								textDecoration: isDeleted ? 'line-through' : 'underline',
							}}
						>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									textDecoration: isDeleted ? 'line-through' : 'none',
									color: isDeleted ? 'red' : 'inherit',
								}}
							>
								{params.value}
								{code === '9999' && <Box sx={{ ml: 2 }}>{dateCompare}</Box>}
							</Box>
						</button>
					)
				},
			},
			{
				field: 'isDeleted',
				headerName: '삭제여부',
				width: 100,
				type: 'string',
				renderCell: (params) => {
					return params.value ? (
						<Chip
							label="삭제됨"
							color="error"
							variant="outlined"
							sx={{ width: 60 }}
						/>
					) : null
				},
			},
			{
				field: 'regId',
				headerName: '작성자',
				width: 150,
			},
			{
				field: 'createdAt',
				headerName: '작성일',
				type: 'string',
				width: 180,
				renderCell: (params) => {
					// 작성일이 오늘이면 시분초만 표기하고 아니면 년월일 표기
					const createdDate = new Date(params.value)
					const now = new Date()
					if (
						createdDate.getFullYear() === now.getFullYear() &&
						createdDate.getMonth() === now.getMonth() &&
						createdDate.getDate() === now.getDate()
					) {
						// 오늘 작성된 글
						return createdDate.toLocaleTimeString()
					} else {
						// 오늘이 아닌 경우
						return createdDate.toLocaleDateString()
					}
				},
			},
			{
				field: 'updatedAt',
				headerName: '수정일',
				type: 'string',
				width: 180,
				renderCell: (params) => {
					// 작성일이 오늘이면 시분초만 표기하고 아니면 년월일 표기하고 작성일과 수정일이 같으면 null 리턴
					const { createdAt, updatedAt } = params.row
					const updatedDate = new Date(updatedAt)
					if (createdAt === updatedAt) {
						return null
					} else {
						if (
							updatedDate.getFullYear() === new Date().getFullYear() &&
							updatedDate.getMonth() === new Date().getMonth() &&
							updatedDate.getDate() === new Date().getDate()
						) {
							return updatedDate.toLocaleTimeString()
						} else {
							return updatedDate.toLocaleDateString()
						}
					}
				},
			},
		],
		[boardTypeList], // boardTypeList가 변경되면 gridColumns 재생성
	)

	const [boardDetail, setBoardDetail] = useState<BoardDetailType | null>(null)
	const [boardViewOpen, setBoardViewOpen] = useState(false)
	const boardViewClose = () => {
		setBoardViewOpen(false)
		setBoardDetail(null)
	}

	// 게시물 제목 클릭 이벤트
	const onTitleClick = (id: any) => {
		console.log('Title clicked:', id)
		apiBoardView(id).then((data) => {
			console.log(data)
			setBoardDetail(data)
			setBoardViewOpen(true)
		})
	}

	const dialogModify = () => {
		setBoardViewOpen(false)
		if (boardDetail) {
			setDialogOpen(true)
			console.log('boardDetail:', boardDetail)
			// 폼 데이터 설정
			const formData: any = {
				id: boardDetail.id,
				code: boardDetail.code,
				title: boardDetail.title,
				content: boardDetail.content || '',
				startDate: boardDetail.startDate || null,
				endDate: boardDetail.endDate || null,
				regId: boardDetail.regId,
			}
			// fileList 데이터 설정
			const files: FileItemType[] = boardDetail.files || []
			setFileList(files)
			console.log('formData with regId:', formData)
			// 폼에 데이터 반영
			updateFormData(formData)
		}
	}

	const apiRef = useGridApiRef()
	const [rowSelectionModel, setRowSelectionModel] =
		useState<GridRowSelectionModel>({ type: 'include', ids: new Set() })

	const [disabledAdd] = useState(false)
	const [disabledDel, setDisabledDel] = useState(
		rowSelectionModel.ids.size === 0,
	)
	const [disabledSave] = useState(true)
	const [disabledSearch] = useState(false)
	const [disabledReset] = useState(false)
	const [disabledExcelDownload] = useState(false)
	const [hideAdd] = useState(true)
	const [hideDel] = useState(true)
	const [hideSave] = useState(true)
	const [hideSearch] = useState(true)
	const [hideReset] = useState(true)
	const [hideExcelDownload] = useState(false)

	const queryClient = useQueryClient()

	// 검색 파라미터 상태
	const [search, setSearch] = useState<BoardSearchType>({
		id: 0,
		code: '2000',
		searchType: 'title',
		searchKeyword: '',
		isUse: 'all',
	})

	// 검색 실행 여부 상태 (수동 검색을 위해)
	const [shouldFetch, setShouldFetch] = useState(true)

	// React Query를 사용한 게시판 목록 조회
	const {
		data: boardData = [],
		isLoading,
		isFetching,
		isError,
		error,
		refetch,
	} = useQuery({
		queryKey: qnaKeys.list(search),
		queryFn: async () => {
			console.log('apiBoardList 호출 시작 - 검색 파라미터:', search)
			const result = await apiBoardList(search)
			console.log('apiBoardList 호출 성공 - 결과:', result)
			return result
		},
		enabled: shouldFetch,
		staleTime: 5 * 60 * 1000, // 5분간 캐시 유지
		gcTime: 10 * 60 * 1000, // 10분간 가비지 컬렉션 방지
	})

	// 쿼리 성공 후 shouldFetch를 false로 설정
	useEffect(() => {
		if (boardData && shouldFetch) {
			setShouldFetch(false)
		}
	}, [boardData, shouldFetch])

	// 추가
	const actionAdd = () => {
		setDialogOpen(true)
	}

	// 삭제
	const actionDel = () => {
		const selectedRows = Array.from(rowSelectionModel.ids).map((id) => Number(id))
		console.log('Selected rows to delete:', selectedRows)
		if (selectedRows.length === 0) {
			alertModal('삭제할 게시물을 선택해주세요.', 'warning', () => {})
			return
		}
		confirmAlert(
			'선택한 게시물을 삭제하시겠습니까?',
			() => {
				apiBoardDeleteMultiple(selectedRows).then((res) => {
					timerAlert('삭제가 완료되었습니다.', 'success', () => {})
					actionSearch()
				})
			},
			() => {},
		)
	}

	// 저장
	const actionSave = () => {}

	// 검색 (React Query refetch 사용)
	const actionSearch = useCallback(() => {
		setShouldFetch(true)
		refetch()
	}, [refetch])
	// 초기화
	const actionReset = useCallback(() => {
		const initialSearch = {
			id: 0,
			code: '2000',
			type: 1,
			searchType: 'title',
			searchKeyword: '',
			isUse: 'all',
		}
		setSearch(initialSearch)
		setRowSelectionModel({ type: 'include', ids: new Set() })
		setShouldFetch(true)

		// 그리드 상태 초기화
		if (apiRef.current) {
			apiRef.current.setPage(0)
			apiRef.current.setFilterModel({
				items: [],
			})
			apiRef.current.setSortModel([])
			apiRef.current.setColumnVisibilityModel({})
		}

		// 캐시 무효화 및 새로운 데이터 가져오기
		queryClient.invalidateQueries({
			queryKey: qnaKeys.lists(),
		})
	}, [queryClient, apiRef])

	// 키보드 엔터키 처리
	const onKeyUp = (e: React.KeyboardEvent<HTMLFormElement>) => {
		if (e.code === 'Enter') {
			actionSearch()
		}
	}

	useEffect(() => {
		console.log(cmmdCd)
		const list = extractCmmnCode(cmmdCd, 'BOARD_TYPE', 1) || []
		console.log(list.children)
		setBoardTypeList(list.children)
	}, [cmmdCd]) // cmmdCd가 변경되면 boardTypeList 업데이트

	// 선택된 행에 따른 삭제 버튼 활성화/비활성화
	useEffect(() => {
		setDisabledDel(rowSelectionModel.ids.size === 0)
	}, [rowSelectionModel.ids.size])

	// 검색 파라미터 변경 시 자동 검색 (debounce 적용)
	useEffect(() => {
		const timeoutId = setTimeout(() => {
			if (search.searchKeyword !== '' || search.code !== '2000') {
				setShouldFetch(true)
			}
		}, 500) // 500ms debounce

		return () => clearTimeout(timeoutId)
	}, [search.searchKeyword, search.code, search.searchType])

	// React Hook Form 설정
	const {
		handleSubmit,
		watch,
		setValue,
		reset,
		clearErrors,
		formState: { errors, isValid },
	} = useForm({
		resolver: zodResolver(boardSchema),
		defaultValues: {
			...boardDefaultValues,
			code: '2000', // Qna 페이지의 기본 코드
		},
		mode: 'onChange', // 실시간 검증
	})

	// form 데이터 업데이트 함수 (BoardWrite에서 호출할 수 있도록)
	const updateFormData = useCallback(
		(newData: any) => {
			console.log('updateFormData called with:', newData)
			if (typeof newData === 'function') {
				// setState 함수 형태로 호출된 경우
				const currentData = watch()
				const updatedData = newData(currentData)
				Object.entries(updatedData).forEach(([key, value]) => {
					console.log(`Setting ${key}:`, value)
					setValue(key as any, value as any, {
						shouldValidate: true,
						shouldDirty: true,
					})
				})
			} else {
				// 직접 객체로 호출된 경우
				Object.entries(newData).forEach(([key, value]) => {
					console.log(`Setting ${key}:`, value)
					setValue(key as any, value as any, {
						shouldValidate: true,
						shouldDirty: true,
					})
				})
			}
			// 업데이트 후 현재 폼 상태 확인
			console.log('Form state after update:', watch())
		},
		[setValue, watch],
	)

	// 폼 제출 처리 - 직접 호출되는 함수
	const dialogSave = async () => {
		// dialogSave 호출 시점의 폼 상태 확인
		const currentFormData = watch()
		console.log('dialogSave - Current form data before submit:', currentFormData)

		// 수동으로 폼 검증 실행
		const result = await handleSubmit(
			async (data: BoardType) => {
				console.log('Valid form data:', data)
				console.log('Form errors:', errors)
				console.log('Is form valid:', isValid)

				// regId가 없으면 currentFormData에서 가져오기
				if (!data.regId && currentFormData.regId) {
					data.regId = currentFormData.regId
					console.log('regId restored from currentFormData:', data.regId)
				}

				// fileList 데이터를 data에 포함시키기
				if (fileList.length > 0) {
					data.fileDetailIds = fileList.map((file) => file.id)
				}

				try {
					const response = await apiBoardSave(data)
					console.log('Save response:', response)
					// 성공 시 폼 리셋 또는 목록 새로고침
					if (response) {
						timerAlert('저장이 완료되었습니다.', 'success', () => {
							dialogClose()
							actionSearch()
						})
					}
				} catch (error) {
					console.error('Save error:', error)
				}
			},
			(validationErrors) => {
				console.log('Form validation errors:', validationErrors)

				// 검증 에러 메시지 생성
				Object.entries(validationErrors)
					.map(([field, error]) => {
						console.log(field, error)
						const fieldNames: Record<string, string> = {
							title: '제목',
							content: '내용',
							code: '분류',
							startDate: '시작일',
							endDate: '종료일',
						}
						const fieldName = fieldNames[field] || field
						return `${fieldName}: ${error?.message || '입력값이 올바르지 않습니다.'}`
					})
					.join('\n')
			},
		)()

		// result를 반환 (필요한 경우)
		return result
	}

	const [dialogOpen, setDialogOpen] = useState(false)
	const dialogClose = () => {
		// 팝업 닫기
		setDialogOpen(false)
		// 폼 데이터 초기화
		reset()
		// 에러 상태 클리어
		clearErrors()
	}

	// 첨부퍼일 목록
	const [fileList, setFileList] = useState<FileItemType[]>([])
	// 파일 업로드 타입
	const [fileUploadType] = useState({
		'image/*': ['.gif', '.jpg', '.jpeg', '.png'],
	})

	return (
		<PageContainer>
			<BoardView
				dialogOpen={boardViewOpen}
				dialogClose={boardViewClose}
				dialogModify={dialogModify}
				dataSet={boardDetail}
			/>
			<BoardWrite
				dialogOpen={dialogOpen}
				dialogClose={dialogClose}
				dialogSave={dialogSave}
				data={watch() as any}
				setData={updateFormData as any}
				bbsType={boardTypeList}
				title="글쓰기"
				errors={errors}
				fileList={fileList}
				setFileList={setFileList}
				isLoading={isLoading}
				fileUploadType={fileUploadType}
				noticeType={[]}
			/>
			<PageTop>
				<PagePosition
					actionAdd={actionAdd}
					actionDel={actionDel}
					actionSave={actionSave}
					actionSearch={actionSearch}
					actionReset={actionReset}
					actionExcelDownload={() => {}}
					disabledAdd={disabledAdd}
					disabledDel={disabledDel}
					disabledSave={disabledSave}
					disabledSearch={disabledSearch}
					disabledReset={disabledReset}
					disabledExcelDownload={disabledExcelDownload}
					hideAdd={hideAdd}
					hideDel={hideDel}
					hideSave={hideSave}
					hideSearch={hideSearch}
					hideReset={hideReset}
					hideExcelDownload={hideExcelDownload}
				/>
				<SearchContainer onKeyUp={onKeyUp}>
					<Grid
						container
						spacing={1}
					>
						<Grid size={4}>
							<TextField
								label="게시판 유형"
								select
								fullWidth
								value={search.code}
								onChange={(e) =>
									setSearch({
										...search,
										code: e.target.value,
									})
								}
							>
								<MenuItem value="0">전체</MenuItem>
								{boardTypeList.map((row: CmmnCodeType, index: number) => {
									return (
										<MenuItem
											key={index}
											value={row.cmmnCd}
										>
											{row.cmmnNm}
										</MenuItem>
									)
								})}
							</TextField>
						</Grid>
						<Grid size={4}>
							<TextField
								label="검색 유형"
								select
								fullWidth
								value={search.searchType}
								onChange={(e) =>
									setSearch({
										...search,
										searchType: e.target.value,
									})
								}
							>
								<MenuItem value="title">제목으로 검색</MenuItem>
								<MenuItem value="content">내용으로 검색</MenuItem>
							</TextField>
						</Grid>
						<Grid size={4}>
							<TextField
								label="검색어"
								variant="outlined"
								fullWidth
								value={search.searchKeyword}
								onChange={(e) =>
									setSearch({
										...search,
										searchKeyword: e.target.value,
									})
								}
							/>
						</Grid>
					</Grid>
				</SearchContainer>
			</PageTop>
			<GridContainer>
				{isError && (
					<Box sx={{ mb: 2 }}>
						<Alert severity="error">
							데이터를 불러오는 중 오류가 발생했습니다: {error?.message}
						</Alert>
					</Box>
				)}
				<StyledDataGrid
					apiRef={apiRef}
					checkboxSelection
					showToolbar
					columns={gridColumns}
					rows={boardData}
					rowSelectionModel={rowSelectionModel}
					onRowSelectionModelChange={(newRowSelectionModel) => {
						setRowSelectionModel(newRowSelectionModel)
					}}
					// disableMultipleRowSelection
					disableRowSelectionOnClick
					loading={isFetching}
					localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
					initialState={{
						columns: {
							columnVisibilityModel: {
								id: false,
							},
						},
					}}
				/>
			</GridContainer>
		</PageContainer>
	)
}

export default Qna
